<template>
  <v-dialog v-model="dialog" persistent :width="$vuetify.display.smAndDown ? 400 : 677">
    <AlFormCard title="Vehicle Model Detail" :subtitle="subtitle">
      <template #error>
        <ErrorAlert
          v-if="error != null"
          :error="error"
          @clearErrors="clearErrors()"
          class="mt-0 mb-4 rounded"
        />
      </template>

      <template #appendAction>
        <v-btn
          variant="tonal"
          class="rounded"
          size="32"
          @click="closeDialog()"
          icon
          color="default"
          density="comfortable"
        >
          <v-icon size="18" icon="mdi-close" />
        </v-btn>
      </template>

      <v-form @submit.prevent ref="form" v-model="valid" validate-on="submit lazy">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="vehicleModelData.name"
              label="Name*"
              variant="outlined"
              hide-details="auto"
              density="compact"
              :rules="[requiredValidator]"
              @update:model-value="clearErrors()"
              :error-messages="error && (error.fieldErrors['name'] as string[])"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-form>

      <template #actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" :loading="loading" @click="save()">Save</v-btn>
      </template>
    </AlFormCard>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { cloneDeep } from 'lodash'
import { diffDeep } from '@/utils/helpers'
import {
  useCreateVehicleModel,
  useUpdateVehicleModel,
  type IVehicleModelPayload
} from '@/composables/vehicle-model'
import { requiredValidator } from '@/utils/validators'

import {
  VehicleModel,
  type IVehicleModel,
  type IVehicleModelData
} from '@/models/fleet-management/vehicle'
import type { ISystemError } from '@/models/error'
import type { VForm } from 'vuetify/components'

import AlFormCard from '@/components/common/AlFormCard.vue'
import ErrorAlert from '@/components/common/ErrorAlert.vue'

interface Props {
  vehicleModel?: IVehicleModel
  vehicleMakeId?: number
  isEdit: boolean
}

const props = defineProps<Props>()

const dialog = defineModel<boolean>('dialog', { default: false })

const vehicleModelData = ref<IVehicleModelData>({ make: props.vehicleMakeId })

watch(dialog, (value) => {
  if (value) {
    clearErrors()
    vehicleModelData.value = !props.isEdit
      ? ({ make: props.vehicleMakeId } as IVehicleModelData)
      : cloneDeep(props.vehicleModel!)
  }
})

const form = ref<typeof VForm>()
const valid = ref(false)

const subtitle = computed(() => `${props.isEdit ? 'Update' : 'Create'} vehicle model information`)

function closeDialog() {
  clearErrors()
  dialog.value = false
}

const {
  isPending: isUpdatePending,
  error: updateError,
  mutate: updateVehicleModel,
  reset: updateReset
} = useUpdateVehicleModel()
const {
  isPending: isCreatePending,
  error: createError,
  mutate: createVehicleModel,
  reset: createReset
} = useCreateVehicleModel()

const localError = ref<ISystemError | null>(null)

const loading = computed(() => (props.isEdit ? isUpdatePending.value : isCreatePending.value))

const error = computed({
  get: () => {
    return localError.value || props.isEdit ? updateError.value : createError.value
  },
  set: (value) => {
    localError.value = value
  }
})

function clearErrors() {
  props.isEdit ? updateReset() : createReset()
  form.value?.resetValidation()
  error.value = null
}

async function save() {
  clearErrors()
  const { valid } = await form.value!.validate()

  if (valid) {
    let vehicleInstance: IVehicleModel | null = null

    try {
      vehicleInstance = new VehicleModel(vehicleModelData.value)
    } catch (e: any) {
      console.log(e)

      // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
      error.value = e
    }

    if (vehicleInstance != null) {
      const saveAction = props.isEdit ? updateVehicleModel : createVehicleModel

      let payload = vehicleInstance
      if (props.isEdit) {
        payload = diffDeep(vehicleInstance, props.vehicleModel, true)
      }

      saveAction({ makeId: props.vehicleMakeId!, vehicleModel: payload } as IVehicleModelPayload, {
        onSuccess: () => {
          closeDialog()
        }
      })
    }
  }
}
</script>
